import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  registerRequest,
  loginRequest,
  verifyTokenRequest,
  updateProfileRequest,
  logoutRequest,
} from "../api/auth";
import {
  storefrontIdentityKey,
  subscribeStorefrontIdentity,
} from "../config/storefrontRuntime";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

function responseCustomer(response) {
  return response?.data?.user || response?.data?.customer || null;
}

function responseError(error, fallback) {
  const data = error?.response?.data;
  if (Array.isArray(data)) return data;
  if (typeof data === "string") return [data];
  if (data?.message) return [data.message];
  return [fallback];
}

function stableSessionValue(value) {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(stableSessionValue);
  if (typeof value !== "object") return value;

  return Object.keys(value)
    .sort()
    .reduce((output, key) => {
      output[key] = stableSessionValue(value[key]);
      return output;
    }, {});
}

function sessionFingerprint(customer) {
  if (!customer) return "anonymous";
  try {
    return JSON.stringify(stableSessionValue(customer));
  } catch {
    return String(customer?._id || customer?.id || customer?.email || "customer");
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [identityKey, setIdentityKey] = useState(() => storefrontIdentityKey());
  const [pricingRevision, setPricingRevision] = useState(0);
  const requestVersionRef = useRef(0);
  const authMutationRef = useRef(0);
  const sessionFingerprintRef = useRef("anonymous");
  const lastSessionCheckAtRef = useRef(0);

  const touchPricing = useCallback(() => {
    setPricingRevision((current) => current + 1);
  }, []);

  const syncPricingSession = useCallback((customer) => {
    const nextFingerprint = sessionFingerprint(customer);
    if (nextFingerprint === sessionFingerprintRef.current) return false;
    sessionFingerprintRef.current = nextFingerprint;
    touchPricing();
    return true;
  }, [touchPricing]);

  const clearIdentity = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const refreshSession = useCallback(async ({ showLoading = false } = {}) => {
    if (!showLoading && authMutationRef.current) return null;

    const now = Date.now();
    // focus + visibilitychange suelen dispararse juntos. Una ventana breve evita
    // duplicar /me y toda la cascada de renders sin perder control de sesión.
    if (!showLoading && now - lastSessionCheckAtRef.current < 30000) return null;
    lastSessionCheckAtRef.current = now;

    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    if (showLoading) setLoading(true);

    try {
      const response = await verifyTokenRequest();
      if (requestVersion !== requestVersionRef.current) return null;
      const customer = responseCustomer(response);
      if (response.status === 200 && customer) {
        setIsAuthenticated(true);
        setUser(customer);
        syncPricingSession(customer);
      } else {
        clearIdentity();
        syncPricingSession(null);
      }
      return customer;
    } catch {
      if (requestVersion !== requestVersionRef.current) return null;
      clearIdentity();
      syncPricingSession(null);
      return null;
    } finally {
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  }, [clearIdentity, syncPricingSession]);

  const signup = async (payload) => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    authMutationRef.current = requestVersion;
    setLoading(true);
    setErrors([]);
    try {
      const response = await registerRequest(payload);
      if (requestVersion !== requestVersionRef.current) return false;
      const customer = responseCustomer(response);
      if (response.status === 201 && customer) {
        const authenticated = response?.data?.authenticated !== false && customer.status === "active";
        setUser(authenticated ? customer : null);
        setIsAuthenticated(authenticated);
        syncPricingSession(authenticated ? customer : null);
      }
      return response?.data;
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) return false;
      clearIdentity();
      syncPricingSession(null);
      const data = error?.response?.data || {};
      if (["CUSTOMER_MATCH_REVIEW_REQUIRED", "CUSTOMER_LINK_VERIFICATION_REQUIRED"].includes(data?.code)) {
        setErrors([]);
      }
      else setErrors(responseError(error, "Error en registro"));
      return {
        ok: false,
        ...data,
        status: error?.response?.status || 0,
      };
    } finally {
      if (authMutationRef.current === requestVersion) authMutationRef.current = 0;
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  };

  const signin = async (credentials) => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    authMutationRef.current = requestVersion;
    setLoading(true);
    setErrors([]);
    try {
      const response = await loginRequest(credentials);
      if (requestVersion !== requestVersionRef.current) return false;
      const customer = responseCustomer(response);
      if (response.status === 200 && customer) {
        setIsAuthenticated(true);
        setUser(customer);
        syncPricingSession(customer);
        return response.data;
      }
      clearIdentity();
      syncPricingSession(null);
      setErrors([response.data?.message || "Error de inicio de sesión"]);
      return false;
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) return false;
      clearIdentity();
      syncPricingSession(null);
      setErrors(responseError(error, "Error de inicio de sesión"));
      return false;
    } finally {
      if (authMutationRef.current === requestVersion) authMutationRef.current = 0;
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  };

  const updateProfile = async (profile) => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    authMutationRef.current = requestVersion;
    setLoading(true);
    setErrors([]);
    try {
      const response = await updateProfileRequest(profile);
      if (requestVersion !== requestVersionRef.current) {
        return { ok: false, message: "La tienda cambió durante la solicitud." };
      }
      const customer = responseCustomer(response);
      if (response.status === 200 && customer) {
        setUser(customer);
        setIsAuthenticated(true);
        syncPricingSession(customer);
        return { ok: true, user: customer, data: response.data };
      }
      return { ok: false, message: response?.data?.message || "No se pudo actualizar el perfil." };
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) {
        return { ok: false, message: "La tienda cambió durante la solicitud." };
      }
      const nextErrors = responseError(error, "No se pudo actualizar el perfil.");
      setErrors(nextErrors);
      if (error?.response?.status === 401) {
        clearIdentity();
        syncPricingSession(null);
      }
      return { ok: false, message: nextErrors[0] };
    } finally {
      if (authMutationRef.current === requestVersion) authMutationRef.current = 0;
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  };

  const logout = async () => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    authMutationRef.current = requestVersion;
    setLoading(true);
    setErrors([]);
    try {
      await logoutRequest();
      if (requestVersion !== requestVersionRef.current) return false;
      clearIdentity();
      syncPricingSession(null);
      return true;
    } catch (error) {
      if (requestVersion !== requestVersionRef.current) return false;
      setErrors(responseError(error, "Error al cerrar sesión"));
      return false;
    } finally {
      if (authMutationRef.current === requestVersion) authMutationRef.current = 0;
      if (requestVersion === requestVersionRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (errors.length === 0) return undefined;
    const timer = setTimeout(() => setErrors([]), 10000);
    return () => clearTimeout(timer);
  }, [errors]);

  useEffect(
    () => subscribeStorefrontIdentity((identity) => {
      requestVersionRef.current += 1;
      clearIdentity();
      syncPricingSession(null);
      setLoading(true);
      setErrors([]);
      setIdentityKey(storefrontIdentityKey(identity));
    }),
    [clearIdentity, syncPricingSession]
  );

  useEffect(() => {
    refreshSession({ showLoading: true });
  }, [identityKey, refreshSession]);

  useEffect(() => {
    const refresh = () => refreshSession({ showLoading: false });
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        errors,
        pricingRevision,
        refreshSession,
        signup,
        signin,
        updateProfile,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
